/**
 * Login Page Component
 *
 * Handles the login callback from Identity Service
 * After successful login, redirects to the main chat page
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button } from '@mui/material';
import { Psychology as AgentIcon, Login as LoginIcon } from '@mui/icons-material';
import useAuth from '../hooks/useAuth';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, login, checkAuth } = useAuth();

  useEffect(() => {
    // Check if we're coming back from Identity Service login
    const returnUrl = searchParams.get('return_url');

    if (isAuthenticated && !isLoading) {
      // Already authenticated, redirect to main page or return URL
      navigate(returnUrl || '/', { replace: true });
    } else if (!isLoading && !isAuthenticated) {
      // Not authenticated, check again in case the session was just set
      checkAuth().then(({ isAuthenticated }) => {
        if (isAuthenticated) {
          navigate(returnUrl || '/', { replace: true });
        }
      });
    }
  }, [isAuthenticated, isLoading, navigate, searchParams, checkAuth]);

  const handleLogin = () => {
    const returnUrl = searchParams.get('return_url') || '/';
    login(returnUrl);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={48} color="primary" />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <AgentIcon sx={{ fontSize: 48, color: 'white' }} />
        </Box>

        <Typography variant="h4" gutterBottom fontWeight={700}>
          QA AI Agent
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          AI-powered Quality Assurance automation with 23 specialized tools
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<LoginIcon />}
          onClick={handleLogin}
          fullWidth
          sx={{ py: 1.5 }}
        >
          Sign in with Identity Service
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          You will be redirected to the Identity Service to authenticate
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage;
